import { useEffect, useState } from 'react';
import { Routes, Route, useSearchParams, useParams, useNavigate } from 'react-router-dom';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import MapCanvas from './components/map/MapCanvas';
import AdminPage from './components/admin/AdminPage';
import { useAuthStore } from './store/authStore';
import { useMapStore } from './store/mapStore';
import { apiUrl } from './lib/config';
import type { MapType } from './types';

// Placeholder pages - to be implemented
function HomePage() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const { isAuthenticated } = useAuthStore();

  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold">WH Mapper</h1>
        <p className="mb-8 text-gray-400">
          Real-time collaborative wormhole mapping for EVE Online
        </p>

        {error && (
          <div className="mb-6 rounded-lg bg-red-900/50 p-4 text-red-200">
            Authentication failed: {decodeURIComponent(error)}
          </div>
        )}

        {isAuthenticated ? (
          <a
            href="/dashboard"
            className="rounded-lg bg-green-600 px-6 py-3 font-semibold text-white transition hover:bg-green-700"
          >
            Go to Dashboard
          </a>
        ) : (
          <a
            href={apiUrl('/api/auth/login')}
            className="rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700"
          >
            Login with EVE Online
          </a>
        )}
      </div>
    </div>
  );
}

function DashboardPage() {
  const { character } = useAuthStore();
  const { maps, mapsLoading, mapsError, fetchMaps, createMap, deleteMap } = useMapStore();
  const navigate = useNavigate();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMapName, setNewMapName] = useState('');
  const [newMapType, setNewMapType] = useState<MapType>('private');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    fetchMaps();
  }, [fetchMaps]);

  const handleCreateMap = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMapName.trim()) {
      setCreateError('Map name is required');
      return;
    }

    setCreating(true);
    setCreateError(null);

    const mapId = await createMap(newMapName.trim(), newMapType);
    if (mapId) {
      setShowCreateModal(false);
      setNewMapName('');
      setNewMapType('private');
      navigate(`/map/${mapId}`);
    } else {
      setCreateError('Failed to create map');
    }
    setCreating(false);
  };

  const handleDeleteMap = async (mapId: string, mapName: string) => {
    if (confirm(`Are you sure you want to delete "${mapName}"? This cannot be undone.`)) {
      await deleteMap(mapId);
    }
  };

  return (
    <div className="p-8">
      <h1 className="mb-4 text-2xl font-bold">Dashboard</h1>
      {character && (
        <div className="mb-6 rounded-lg bg-gray-800 p-4">
          <p className="text-lg">
            Welcome, <span className="font-semibold text-blue-400">{character.name}</span>
          </p>
          {character.corporationName && (
            <p className="text-sm text-gray-400">
              {character.corporationName}
              {character.allianceName && ` [${character.allianceName}]`}
            </p>
          )}
        </div>
      )}

      {/* Maps Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Your Maps</h2>
          <button
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
            onClick={() => setShowCreateModal(true)}
          >
            Create New Map
          </button>
        </div>

        {mapsLoading ? (
          <div className="text-gray-400">Loading maps...</div>
        ) : mapsError ? (
          <div className="text-red-400">Error: {mapsError}</div>
        ) : maps.length === 0 ? (
          <div className="rounded-lg bg-gray-800 p-6 text-center text-gray-400">
            No maps yet. Create your first map to get started!
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {maps.map((map) => (
              <div
                key={map.id}
                className="rounded-lg bg-gray-800 p-4 transition hover:bg-gray-750"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => navigate(`/map/${map.id}`)}
                  >
                    <h3 className="font-semibold text-white">{map.name}</h3>
                    <p className="text-sm text-gray-400">
                      {map.type === 'private' ? 'Private' : map.type === 'corporation' ? 'Corporation' : map.type === 'alliance' ? 'Alliance' : 'Whitelisted'}
                      {' · '}{map.systemCount} system{map.systemCount !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-gray-500">
                      Created {new Date(map.createdAt * 1000).toLocaleDateString()}
                    </p>
                  </div>
                  {map.role === 'owner' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMap(map.id, map.name);
                      }}
                      className="text-gray-500 hover:text-red-400 transition"
                      title="Delete map"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Map Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-gray-800 p-6">
            <h2 className="mb-4 text-xl font-semibold">Create New Map</h2>
            <form onSubmit={handleCreateMap}>
              <div className="mb-4">
                <label className="mb-2 block text-sm text-gray-400">Map Name</label>
                <input
                  type="text"
                  value={newMapName}
                  onChange={(e) => setNewMapName(e.target.value)}
                  className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="Enter map name"
                  autoFocus
                />
              </div>
              <div className="mb-4">
                <label className="mb-2 block text-sm text-gray-400">Map Type</label>
                <select
                  value={newMapType}
                  onChange={(e) => setNewMapType(e.target.value as MapType)}
                  className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                >
                  <option value="private">Private (only you)</option>
                  <option value="whitelisted">Whitelisted (all whitelisted users)</option>
                  <option value="corporation">Corporation (corp members)</option>
                  <option value="alliance">Alliance (alliance members)</option>
                </select>
              </div>
              {createError && (
                <div className="mb-4 text-sm text-red-400">{createError}</div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewMapName('');
                    setCreateError(null);
                  }}
                  className="flex-1 rounded-lg bg-gray-700 px-4 py-2 text-white transition hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MapPage() {
  const { mapId } = useParams<{ mapId: string }>();
  const { loadMap, unloadMap, currentMap, mapLoading, mapError } = useMapStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (mapId) {
      loadMap(mapId);
    }
    return () => {
      unloadMap();
    };
  }, [mapId, loadMap, unloadMap]);

  if (mapLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-400">Loading map...</div>
      </div>
    );
  }

  if (mapError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">Error: {mapError}</div>
        <button
          onClick={() => navigate('/dashboard')}
          className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!currentMap) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-gray-400">Map not found</div>
        <button
          onClick={() => navigate('/dashboard')}
          className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="h-full">
      <MapCanvas />
    </div>
  );
}

function App() {
  // Check authentication status on app load
  useEffect(() => {
    useAuthStore.getState().checkAuth();
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="map/:mapId"
          element={
            <ProtectedRoute>
              <MapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute requireWhitelist={false}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;
